import { Eye, GripVertical, ImageIcon, Images, Loader2, PenLine, Upload, X } from "lucide-react"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { SCENE_PRIMITIVE_SHAPES } from "@/components/locations/location-scene-primitives"
import { ReferenceMediaPreview } from "@/components/reference-inputs/reference-media-preview"
import { Button } from "@/components/ui/primitives"
import {
  canvasReferenceKey,
  entityPickKey,
  referencePickerCandidateUnavailable,
} from "@/lib/canvas-reference-candidates"
import { CANVAS_REFERENCE_DRAG_TYPE, referenceToDragPayload } from "@/lib/canvas-drag"
import type { ScenePrimitiveShape } from "@/lib/location-scene-3d/scene-state"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import type { CanvasReferenceItem } from "@/types/canvas"
import type { PickerTab } from "./reference-picker-types"
import { ReferencePickerTabs } from "./reference-picker-tabs"

export type ReferencePickerContentProps = {
  tabs: PickerTab[]
  pickerTab: PickerTab
  onTabChange: (tab: PickerTab) => void
  candidates: CanvasReferenceItem[]
  existingKeys: string[]
  enableDrag: boolean
  onClose: () => void
  onPick: (ref: CanvasReferenceItem) => void
  onPickPrimitive?: (shape: ScenePrimitiveShape) => void
  previewRef: CanvasReferenceItem | null
  onPreviewRefChange: (ref: CanvasReferenceItem | null) => void
  onUploadClick?: () => void
  uploadUploading?: boolean
  /** «Выбрать из библиотеки» — вторая точка входа рядом с загрузкой файла. */
  onLibraryPickClick?: () => void
  onSketchClick?: () => void
  onSketchEditRef?: (ref: CanvasReferenceItem) => void
  /** Image eye: host opens sketch view / lightbox. Video uses built-in lightbox. */
  onPreviewRef?: (ref: CanvasReferenceItem) => void
  /**
   * Z ladder for the built-in video lightbox (e.g. 3 above immersive 3D stage).
   * Defaults to nested=true (above ShotPanel).
   */
  previewLightboxNested?: boolean | 2 | 3
  /** Outer padding for framed placements; false for embedded in-parent. */
  padded?: boolean
  /** Close (X) control — hide when embedded in a parent dock/panel. */
  showClose?: boolean
  title?: string
  subtitle?: string
  /** Ignore attachable/preview gates (scene entity placement). */
  entityPick?: boolean
  /**
   * Fill a height-capped portal parent so overflow-y-auto gets a definite height
   * (body-portaled scene picker under Dialog RemoveScroll).
   */
  constrainToParent?: boolean
}

export function ReferencePickerContent({
  tabs,
  pickerTab,
  onTabChange,
  candidates,
  existingKeys,
  enableDrag,
  onClose,
  onPick,
  onPickPrimitive,
  previewRef,
  onPreviewRefChange,
  onUploadClick,
  uploadUploading = false,
  onLibraryPickClick,
  onSketchClick,
  onSketchEditRef,
  onPreviewRef,
  previewLightboxNested = true,
  padded = true,
  showClose = true,
  title,
  subtitle,
  entityPick = false,
  constrainToParent = false,
}: ReferencePickerContentProps) {
  const { t } = useTranslation(["workspace", "common"])
  const heading = title ?? t("canvas.referencesModal.title")
  const subheading = subtitle ?? t("canvas.referencesModal.subtitle")
  const showUploadActions = onUploadClick != null || onLibraryPickClick != null || onSketchClick != null
  const showPrimitives = pickerTab === "primitive" && onPickPrimitive != null

  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-3",
          constrainToParent ? "h-full max-h-[min(50vh,320px,100%)] min-h-0" : "max-h-[min(50vh,320px)]",
          padded ? "p-3" : "p-0",
        )}
      >
        <div className={cn("flex shrink-0 items-start gap-3", showClose && "justify-between")}>
          <div>
            <p className="text-sm font-semibold text-ui-text-primary">{heading}</p>
            {subheading ? <p className="mt-0.5 text-xs text-ui-text-secondary">{subheading}</p> : null}
          </div>
          {showClose ? (
            <button
              type="button"
              className="shrink-0 rounded-lg p-1 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
              aria-label={t("canvas.modal.close")}
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <ReferencePickerTabs
          tabs={tabs}
          pickerTab={pickerTab}
          onTabChange={onTabChange}
          upload={
            showUploadActions
              ? ({ compact }) => {
                  const actionClass = compact
                    ? "h-7 w-7 shrink-0 gap-0 px-0"
                    : "h-7 shrink-0 gap-1.5 px-2.5 text-[11px]"
                  const libraryLabel = t("canvas.referencesModal.pickFromLibrary")
                  const uploadLabel = t("canvas.referencesModal.upload")
                  const sketchLabel = t("canvas.referencesModal.sketch")
                  return (
                    <>
                      {/* Библиотека первая (готовый ассет), эскиз, затем загрузка с диска. */}
                      {onLibraryPickClick != null ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={actionClass}
                          aria-label={libraryLabel}
                          title={libraryLabel}
                          onClick={onLibraryPickClick}
                        >
                          <Images className="h-3.5 w-3.5" />
                          {compact ? null : libraryLabel}
                        </Button>
                      ) : null}
                      {onSketchClick != null ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={actionClass}
                          disabled={uploadUploading}
                          aria-label={sketchLabel}
                          title={sketchLabel}
                          onClick={onSketchClick}
                        >
                          <PenLine className="h-3.5 w-3.5" />
                          {compact ? null : sketchLabel}
                        </Button>
                      ) : null}
                      {onUploadClick != null ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="default"
                          className={actionClass}
                          disabled={uploadUploading}
                          aria-busy={uploadUploading}
                          aria-label={uploadLabel}
                          title={uploadLabel}
                          onClick={onUploadClick}
                        >
                          {uploadUploading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Upload className="h-3.5 w-3.5" />
                          )}
                          {compact ? null : uploadLabel}
                        </Button>
                      ) : null}
                    </>
                  )
                }
              : undefined
          }
        />

        <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto">
          {showPrimitives ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(4.75rem,1fr))] gap-2">
              {SCENE_PRIMITIVE_SHAPES.map(({ shape, icon: Icon }) => {
                const label = t(`locationsPage.scene3d.primitive.${shape}`)
                return (
                  <div
                    key={shape}
                    role="button"
                    tabIndex={0}
                    onClick={() => onPickPrimitive(shape)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        onPickPrimitive(shape)
                      }
                    }}
                    className="flex w-full min-w-0 cursor-pointer flex-col items-center gap-1 rounded-lg border border-ui-border bg-ui-surface p-1 text-center hover:border-ui-border-strong"
                    title={label}
                  >
                    <div className="flex h-12 w-full items-center justify-center rounded bg-ui-surface-sunken text-ui-text-primary">
                      <Icon className="h-5 w-5" aria-hidden />
                    </div>
                    <span className="w-full truncate text-[10px] text-ui-text-secondary">{label}</span>
                  </div>
                )
              })}
            </div>
          ) : candidates.length > 0 ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(4.75rem,1fr))] gap-2">
              {candidates.map((ref) => {
                const unavailable = entityPick
                  ? existingKeys.includes(entityPickKey(ref))
                  : referencePickerCandidateUnavailable(ref, existingKeys)
                const hasPreview = Boolean(ref.url?.trim())
                const showEye = hasPreview && (ref.media_type === "video" || onPreviewRef != null)
                return (
                  <div
                    key={canvasReferenceKey(ref)}
                    role="button"
                    tabIndex={unavailable ? -1 : 0}
                    aria-disabled={unavailable}
                    draggable={enableDrag && !unavailable}
                    onClick={() => {
                      if (unavailable) return
                      onPick(ref)
                    }}
                    onKeyDown={(e) => {
                      if (unavailable) return
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        onPick(ref)
                      }
                    }}
                    onDragStart={(e) => {
                      if (!enableDrag || unavailable) return
                      e.dataTransfer.setData(CANVAS_REFERENCE_DRAG_TYPE, JSON.stringify(referenceToDragPayload(ref)))
                      e.dataTransfer.effectAllowed = "copy"
                      const img = (e.currentTarget as HTMLElement).querySelector("img, video")
                      if (img && img.clientWidth > 0 && img.clientHeight > 0) {
                        e.dataTransfer.setDragImage(img, img.clientWidth / 2, img.clientHeight / 2)
                      }
                    }}
                    className={cn(
                      "group/cand relative flex w-full min-w-0 flex-col items-center gap-1 rounded-lg border border-ui-border bg-ui-surface p-1 text-center hover:border-ui-border-strong",
                      unavailable
                        ? "cursor-not-allowed opacity-40"
                        : enableDrag
                          ? "cursor-grab active:cursor-grabbing"
                          : "cursor-pointer",
                    )}
                    title={ref.label}
                  >
                    {enableDrag && !unavailable ? (
                      <span
                        aria-hidden
                        className="pointer-events-none absolute top-0.5 left-0.5 z-10 rounded bg-black/55 p-0.5 text-white opacity-0 transition-opacity group-hover/cand:opacity-100"
                      >
                        <GripVertical className="h-3 w-3" />
                      </span>
                    ) : null}
                    {showEye ? (
                      <button
                        type="button"
                        className="pointer-events-auto absolute top-0.5 right-0.5 z-10 rounded bg-black/55 p-0.5 text-white opacity-0 transition-opacity group-hover/cand:opacity-100"
                        aria-label={t("canvas.panel.previewReference")}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (ref.media_type === "video") {
                            onPreviewRefChange(ref)
                            return
                          }
                          onPreviewRef?.(ref)
                        }}
                      >
                        <Eye className="h-3 w-3" />
                      </button>
                    ) : null}
                    {onSketchEditRef && ref.media_type !== "video" && ref.url?.trim() ? (
                      <button
                        type="button"
                        className="pointer-events-auto absolute bottom-6 left-0.5 z-10 rounded bg-black/55 p-0.5 text-white opacity-0 transition-opacity group-hover/cand:opacity-100"
                        aria-label={t("canvas.sketch.editInSketch")}
                        title={t("canvas.sketch.editInSketch")}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation()
                          onSketchEditRef(ref)
                        }}
                      >
                        <PenLine className="h-3 w-3" />
                      </button>
                    ) : null}
                    {ref.url ? (
                      <ReferenceMediaPreview
                        url={ref.url}
                        thumbUrl={ref.thumb_url}
                        mediaType={ref.media_type}
                        className="h-12 w-full rounded object-cover"
                        showVideoBadge
                      />
                    ) : (
                      <div className="flex h-12 w-full items-center justify-center rounded bg-ui-surface-sunken text-ui-text-muted">
                        <ImageIcon className="h-5 w-5" aria-hidden />
                      </div>
                    )}
                    <span className="w-full truncate text-[10px] text-ui-text-secondary">{ref.label}</span>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-ui-text-muted">{t("canvas.panel.pickerEmpty")}</p>
          )}
        </div>
      </div>

      <ImageLightboxDialog
        open={previewRef != null && previewRef.media_type === "video" && Boolean(previewRef.url)}
        onOpenChange={(openState) => {
          if (!openState) onPreviewRefChange(null)
        }}
        url={null}
        videoUrl={previewRef?.media_type === "video" ? previewRef.url : null}
        title={previewRef?.label ?? null}
        nested={previewLightboxNested}
      />
    </>
  )
}
